import { useState } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import type { Pet, Reserva, Mensagem, DashboardAvaliacoes } from "@/types/dashboard";
import ReservaDetalhesModal, { type ReservaDetalheModal } from "@/components/ReservaDetalhesModal";
import {
  Search,
  Plus,
  PawPrint,
  CalendarDays,
  MessageCircle,
  Edit3,
  ChevronRight,
  MapPin,
  XCircle,
  Star,
  MessageSquareText,
  ShieldCheck,
  TrendingUp,
} from "lucide-react";

function Stars({ value }: { value: number }) {
  const nota = Math.max(0, Math.min(5, Math.round(value || 0)));

  return (
    <div className="flex items-center gap-0.5" aria-label={`Nota ${value} de 5`}>
      {[1, 2, 3, 4, 5].map((star) => (
        <Star
          key={star}
          className={`h-4 w-4 ${
            star <= nota ? "fill-[#d4af37] text-[#d4af37]" : "text-slate-300"
          }`}
          strokeWidth={1.6}
        />
      ))}
    </div>
  );
}

// ---- Hero Banner ----
export function HeroBanner({ firstName }: { firstName: string }) {
  return (
    <div className="gradient-primary rounded-2xl p-6 md:p-8 flex items-center justify-between animate-fade-in-up">
      <div>
        <h1 className="text-2xl md:text-3xl font-extrabold text-primary-foreground">
          Olá, {firstName}! 👋
        </h1>
        <p className="mt-1 text-primary-foreground/80 text-sm md:text-base">
          Gerencie seus pets e reservas em um só lugar.
        </p>
        <div className="flex gap-2 mt-4">
          <Button asChild size="sm" variant="secondary" className="gap-1.5 font-semibold">
            <Link to="/">
              <Search className="h-3.5 w-3.5" />
              Buscar Cuidador
            </Link>
          </Button>

          <Button
            asChild
            size="sm"
            variant="outline"
            className="gap-1.5 font-semibold border-border text-foreground hover:bg-accent hover:text-accent-foreground"
          >
            <Link to="/tutor/pets/create">
              <Plus className="h-3.5 w-3.5" />
              Novo Pet
            </Link>
          </Button>
        </div>
      </div>
      <span className="hidden md:block text-6xl animate-floating">🐶</span>
    </div>
  );
}

// ---- Pets Section ----
export function PetsSection({ pets }: { pets: Pet[] }) {
  return (
    <Card className="card-shadow border-0 animate-fade-in-up stagger-1" style={{ opacity: 0 }}>
      <CardHeader className="flex flex-row items-center justify-between pb-3">
        <CardTitle className="text-lg flex items-center gap-2">
          <PawPrint className="h-5 w-5 text-coral" />
          Meus Pets
        </CardTitle>
        <Button asChild size="sm" variant="secondary" className="gap-1 font-semibold text-xs">
          <Link to="/tutor/pets/create">
            <Plus className="h-3.5 w-3.5" />
            Cadastrar
          </Link>
        </Button>
      </CardHeader>
      <CardContent>
        {pets.length > 0 ? (
          <div className="grid gap-3 sm:grid-cols-2">
            {pets.map((pet) => (
              <div
                key={pet.id}
                className="flex items-center gap-3 p-3 rounded-xl bg-muted/50 hover:bg-muted transition-colors group cursor-pointer"
              >
                <Avatar className="h-12 w-12 rounded-xl">
                  <AvatarImage src={pet.fotoUrl || undefined} />
                  <AvatarFallback className="rounded-xl bg-forest-light text-forest font-bold">
                    {pet.nome.charAt(0)}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm text-foreground truncate">{pet.nome}</p>
                  <p className="text-xs text-muted-foreground">
                    {pet.especie}
                    {pet.raca && ` • ${pet.raca}`}
                  </p>
                </div>
                <Button asChild variant="ghost" size="icon" className="opacity-0 group-hover:opacity-100 transition-opacity">
                  <a href={`/pets/${pet.id}/editar`}>
                    <Edit3 className="h-4 w-4" />
                  </a>
                </Button>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-10">
            <span className="text-5xl animate-bounce-soft inline-block">🐕</span>
            <p className="mt-3 text-muted-foreground text-sm">
              Você ainda não tem pets cadastrados.
            </p>
            <Button asChild size="sm" variant="secondary" className="mt-3 gap-1 font-semibold">
              <Link to="/tutor/pets/create">
                <Plus className="h-3.5 w-3.5" />
                Cadastrar Pet
              </Link>
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ---- Reservas Section ----
const statusColors: Record<string, string> = {
  confirmada: "bg-forest-light text-forest",
  pendente: "bg-muted text-muted-foreground",
  recusada: "bg-destructive/10 text-destructive",
  cancelada: "bg-muted text-muted-foreground",
  concluida: "bg-blue-100 text-blue-700",
  finalizada: "bg-blue-100 text-blue-700",
};

function csrfToken() {
  return document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content || "";
}

export function ReservasSection({ reservas }: { reservas: Reserva[] }) {
  const [modalOpen, setModalOpen] = useState(false);
  const [reservaSelecionada, setReservaSelecionada] = useState<ReservaDetalheModal | null>(null);
  const [reservaCarregandoId, setReservaCarregandoId] = useState<string | null>(null);

  async function abrirDetalhesReserva(reserva: Reserva) {
    try {
      setReservaCarregandoId(reserva.id);

      const resp = await fetch(`/reserva/${reserva.id}/detalhes-json`, {
        credentials: "include",
        headers: {
          Accept: "application/json",
          "X-Requested-With": "XMLHttpRequest",
        },
      });

      if (!resp.ok) {
        throw new Error("Falha ao buscar detalhes da reserva");
      }

      const dados = await resp.json();

      setReservaSelecionada({
        id: dados.id,
        tutor: dados.tutor || "Tutor",
        anfitriao: reserva.anfitriaoNome,
        tutorId: dados.tutor_id || "",
        telefoneTutor: dados.telefone_tutor || "",
        servico: dados.servico || reserva.servico || "Serviço",
        modalidadeServico: dados.modalidade_servico || "",
        status: dados.status || reserva.status || "pendente",
        dataInicio: dados.data_inicio || "",
        dataFim: dados.data_fim || "",
        horaInicio: dados.hora_inicio || "",
        horaFim: dados.hora_fim || "",
        checkinRealizadoEm: dados.checkin_realizado_em || null,
        checkoutRealizadoEm: dados.checkout_realizado_em || null,
        mensagem: dados.mensagem || "",
        valorTotal: dados.valor_total ?? dados.pagamento?.valor_total ?? reserva.valorTotal ?? null,
        valorLiquido: dados.valor_liquido ?? dados.pagamento?.valor_liquido ?? null,
        podeCancelar: Boolean(dados.pode_cancelar),
        cancelarUrl: dados.cancelar_url ?? reserva.cancelarUrl ?? null,
        adicionalCheckoutValor: dados.adicional_checkout_valor ?? null,
        adicionalCheckoutTipo: dados.adicional_checkout_tipo ?? null,
        adicionalCheckoutStatus: dados.adicional_checkout_status ?? null,
        checkoutAdicionalUrl: dados.checkout_adicional_url ?? null,
        checkoutAdicionalExternoUrl: dados.checkout_adicional_externo_url ?? null,
        checkoutAdicionalWhatsappUrl: dados.checkout_adicional_whatsapp_url ?? null,
        avaliacao: dados.avaliacao ?? null,
        pets: Array.isArray(dados.pets) ? dados.pets : [],
        chatUrl: dados.chat_url || "/chat/inbox",
      });

      setModalOpen(true);
    } catch (error) {
      console.error(error);
      alert("Não foi possível carregar os detalhes da reserva.");
    } finally {
      setReservaCarregandoId(null);
    }
  }

  function fecharDetalhesReserva() {
    setModalOpen(false);
    setReservaSelecionada(null);
  }

  return (
    <>
      <Card className="card-shadow border-0 animate-fade-in-up stagger-2" style={{ opacity: 0 }}>
        <CardHeader className="flex flex-row items-center justify-between pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <CalendarDays className="h-5 w-5 text-forest" />
            Reservas
          </CardTitle>
          <Button asChild variant="link" size="sm" className="text-xs font-semibold">
            <Link to="/tutor/dashboard?view=reservas">
              Ver todas <ChevronRight className="h-3.5 w-3.5" />
            </Link>
          </Button>
        </CardHeader>
        <CardContent>
          {reservas.length > 0 ? (
            <div className="space-y-3">
              {reservas.map((r) => (
                <div
                  key={r.id}
                  className="flex items-start gap-3 rounded-xl bg-muted/50 p-3 transition-colors hover:bg-muted"
                >
                  <Avatar className="h-10 w-10">
                    <AvatarImage src={r.anfitriaoFoto || undefined} />
                    <AvatarFallback className="bg-forest-light text-forest text-sm font-bold">
                      {r.anfitriaoNome.charAt(0)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="truncate text-sm font-semibold text-foreground">
                        {r.anfitriaoNome}
                      </span>
                      <span
                        className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${
                          statusColors[r.status] || "bg-muted text-muted-foreground"
                        }`}
                      >
                        {r.status}
                      </span>
                    </div>
                    <p className="mt-0.5 text-xs text-muted-foreground">
                      {r.servico && `${r.servico} • `}
                      {r.qtdPets && `${r.qtdPets} pet(s) • `}
                      {r.periodoFormatado || `${r.dataInicioFormatada || r.dataInicio || "Data não informada"}${r.dataFim && r.dataFim !== r.dataInicio ? ` → ${r.dataFimFormatada || r.dataFim}` : ""}`}
                      {r.horarioFormatado && ` • ${r.horarioFormatado}`}
                      {r.cidade && (
                        <span className="ml-1 inline-flex items-center gap-0.5">
                          <MapPin className="h-3 w-3" />
                          {r.cidade}
                        </span>
                      )}
                    </p>
                    <div className="mt-2 flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-7 text-xs"
                        onClick={() => abrirDetalhesReserva(r)}
                        disabled={reservaCarregandoId === r.id}
                      >
                        {reservaCarregandoId === r.id ? "Carregando..." : "Ver detalhes"}
                      </Button>
                      <Button asChild variant="ghost" size="sm" className="h-7 text-xs">
                        <a href="/chat/inbox">
                          Abrir chat
                        </a>
                      </Button>

                      {r.cancelarUrl && r.checkinRealizadoEm ? (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="h-7 border-gray-200 text-xs text-gray-400"
                          disabled
                        >
                          <XCircle className="h-3.5 w-3.5" />
                          Cancelar
                        </Button>
                      ) : r.podeCancelar && r.cancelarUrl ? (
                        <form
                          action={r.cancelarUrl}
                          method="POST"
                          onSubmit={(event) => {
                            if (!window.confirm("Tem certeza que deseja cancelar esta reserva?")) {
                              event.preventDefault();
                            }
                          }}
                        >
                          <input type="hidden" name="_token" value={csrfToken()} />
                          <Button
                            type="submit"
                            variant="outline"
                            size="sm"
                            className="h-7 border-red-200 text-xs text-red-600 hover:bg-red-50 hover:text-red-700"
                          >
                            <XCircle className="h-3.5 w-3.5" />
                            Cancelar
                          </Button>
                        </form>
                      ) : null}

                      {r.podeAvaliar && r.avaliacaoUrl ? (
                        <Button asChild size="sm" className="h-7 text-xs">
                          <a href={r.avaliacaoUrl}>{r.labelAvaliacao || "Avaliar"}</a>
                        </Button>
                      ) : r.jaAvaliei ? (
                        <Button variant="secondary" size="sm" className="h-7 text-xs" disabled>
                          Avaliação enviada
                        </Button>
                      ) : null}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="py-8 text-center">
              <span className="text-4xl">📅</span>
              <p className="mt-2 text-sm text-muted-foreground">Nenhuma reserva agendada.</p>
            </div>
          )}
        </CardContent>
      </Card>

      <ReservaDetalhesModal
        open={modalOpen}
        reserva={reservaSelecionada}
        onClose={fecharDetalhesReserva}
        viewerRole="tutor"
      />
    </>
  );
}

// ---- Mensagens Section ----
export function MensagensSection({ mensagens }: { mensagens: Mensagem[] }) {
  return (
    <Card className="card-shadow border-0 animate-fade-in-up stagger-3" style={{ opacity: 0 }}>
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2">
          <MessageCircle className="h-5 w-5 text-coral" />
          Mensagens
        </CardTitle>
      </CardHeader>
      <CardContent>
        {mensagens.length > 0 ? (
          <div className="space-y-2">
            {mensagens.map((m) => (
              <div
                key={m.id}
                className="flex items-center justify-between p-3 rounded-xl bg-muted/50 hover:bg-muted transition-colors cursor-pointer"
              >
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-foreground">{m.contatoNome}</p>
                  <p className="text-xs text-muted-foreground truncate">{m.ultimaMensagem}</p>
                </div>
                <Button asChild variant="ghost" size="sm" className="text-xs shrink-0">
                  <Link to="/chat/inbox">
                    Ver
                  </Link>
                </Button>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8">
            <span className="text-4xl">💬</span>
            <p className="mt-2 text-muted-foreground text-sm">Caixa de entrada vazia.</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export function AvaliacoesSectionTutor({
  avaliacoes,
}: {
  avaliacoes?: DashboardAvaliacoes;
}) {
  const dados = avaliacoes ?? {
    media: 0,
    total: 0,
    total_comentarios: 0,
    distribuicao: {},
    recebidas: [],
    feitas: [],
  };

  const recebidasComComentario = dados.recebidas.filter(
    (avaliacao) => avaliacao.tem_comentario || avaliacao.comentario
  );
  const total = Math.max(0, Number(dados.total || 0));
  const media = Number(dados.media || 0);
  const taxaComentarios = total > 0 ? Math.round((dados.total_comentarios / total) * 100) : 0;

  return (
    <div className="space-y-5">
      <section className="rounded-3xl border border-black/5 bg-white p-5 shadow-sm md:p-6">
        <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <p className="text-xs font-bold uppercase tracking-wide text-muted-foreground">
              Reputação nas reservas
            </p>
            <h2 className="mt-1 text-2xl font-extrabold text-foreground">
              O que anfitriões dizem sobre você
            </h2>
            <p className="mt-2 max-w-2xl text-sm leading-6 text-muted-foreground">
              Acompanhe sua nota como tutor, leia os comentários recebidos e consulte
              o histórico das avaliações que você enviou aos anfitriões.
            </p>
          </div>

          <div className="rounded-3xl bg-forest-light px-6 py-5 text-center">
            <div className="flex items-center justify-center gap-2">
              <Star className="h-6 w-6 fill-[#d4af37] text-[#d4af37]" />
              <span className="text-4xl font-extrabold text-foreground">
                {media.toFixed(1)}
              </span>
            </div>
            <p className="mt-1 text-sm font-semibold text-forest">
              {total} avaliação{total === 1 ? "" : "es"} recebida{total === 1 ? "" : "s"}
            </p>
          </div>
        </div>

        <div className="mt-6 grid gap-3 md:grid-cols-3">
          <div className="rounded-2xl border border-slate-100 bg-slate-50 p-4">
            <div className="flex items-center gap-2 text-sm font-semibold text-slate-600">
              <TrendingUp className="h-4 w-4 text-forest" />
              Média geral
            </div>
            <div className="mt-3 flex items-center justify-between gap-3">
              <Stars value={media} />
              <span className="text-xl font-bold text-slate-900">{media.toFixed(1)}</span>
            </div>
          </div>

          <div className="rounded-2xl border border-slate-100 bg-slate-50 p-4">
            <div className="flex items-center gap-2 text-sm font-semibold text-slate-600">
              <MessageSquareText className="h-4 w-4 text-forest" />
              Comentários
            </div>
            <p className="mt-3 text-xl font-bold text-slate-900">
              {dados.total_comentarios}
              <span className="ml-2 text-sm font-medium text-muted-foreground">
                ({taxaComentarios}%)
              </span>
            </p>
          </div>

          <div className="rounded-2xl border border-slate-100 bg-slate-50 p-4">
            <div className="flex items-center gap-2 text-sm font-semibold text-slate-600">
              <ShieldCheck className="h-4 w-4 text-forest" />
              Confiança
            </div>
            <p className="mt-3 text-xl font-bold text-slate-900">
              {total > 0 ? "Ativa" : "Em construção"}
            </p>
          </div>
        </div>

        <div className="mt-6 space-y-2">
          {[5, 4, 3, 2, 1].map((nota) => {
            const count = Number(dados.distribuicao?.[String(nota)] || 0);
            const pct = total > 0 ? Math.round((count / total) * 100) : 0;

            return (
              <div key={nota} className="flex items-center gap-3">
                <span className="w-10 text-sm font-semibold text-slate-600">{nota} ★</span>
                <div className="h-2 flex-1 overflow-hidden rounded-full bg-slate-100">
                  <div
                    className="h-full rounded-full bg-[#d4af37]"
                    style={{ width: `${pct}%` }}
                  />
                </div>
                <span className="w-10 text-right text-xs font-medium text-muted-foreground">
                  {count}
                </span>
              </div>
            );
          })}
        </div>
      </section>

      <section className="rounded-3xl border border-black/5 bg-white p-5 shadow-sm md:p-6">
        <div className="flex flex-col gap-1 md:flex-row md:items-end md:justify-between">
          <div>
            <h3 className="text-lg font-bold text-foreground">Comentários recebidos</h3>
            <p className="text-sm text-muted-foreground">
              Feedbacks deixados por anfitriões depois de reservas concluídas.
            </p>
          </div>
          <span className="text-sm font-semibold text-muted-foreground">
            {recebidasComComentario.length} comentário{recebidasComComentario.length === 1 ? "" : "s"}
          </span>
        </div>

        {recebidasComComentario.length > 0 ? (
          <div className="mt-5 grid gap-4 lg:grid-cols-2">
            {recebidasComComentario.map((avaliacao) => (
              <article key={avaliacao.id} className="rounded-2xl border border-slate-100 bg-slate-50 p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex min-w-0 items-center gap-3">
                    <Avatar className="h-10 w-10">
                      <AvatarFallback className="bg-forest-light text-forest text-sm font-bold">
                        {(avaliacao.pessoa_nome || "A").charAt(0)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="min-w-0">
                      <p className="truncate text-sm font-bold text-foreground">
                        {avaliacao.pessoa_nome}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {avaliacao.servico_nome || "Serviço"} {avaliacao.data ? `• ${avaliacao.data}` : ""}
                      </p>
                    </div>
                  </div>
                  <Stars value={avaliacao.nota} />
                </div>
                <p className="mt-4 text-sm leading-6 text-slate-700">
                  “{avaliacao.comentario}”
                </p>
              </article>
            ))}
          </div>
        ) : (
          <div className="mt-5 rounded-2xl bg-slate-50 p-6 text-center">
            <p className="text-sm font-semibold text-slate-700">
              Ainda não há comentários recebidos.
            </p>
            <p className="mt-1 text-sm text-muted-foreground">
              Quando anfitriões avaliarem suas reservas, os comentários aparecerão aqui.
            </p>
          </div>
        )}
      </section>

      <section className="rounded-3xl border border-black/5 bg-white p-5 shadow-sm md:p-6">
        <div className="flex flex-col gap-1 md:flex-row md:items-end md:justify-between">
          <div>
            <h3 className="text-lg font-bold text-foreground">Avaliações que você fez</h3>
            <p className="text-sm text-muted-foreground">
              Histórico das avaliações enviadas por você aos anfitriões.
            </p>
          </div>
          <span className="text-sm font-semibold text-muted-foreground">
            {dados.feitas.length} enviada{dados.feitas.length === 1 ? "" : "s"}
          </span>
        </div>

        {dados.feitas.length > 0 ? (
          <div className="mt-5 divide-y divide-slate-100 overflow-hidden rounded-2xl border border-slate-100">
            {dados.feitas.map((avaliacao) => (
              <article key={avaliacao.id} className="bg-white p-4">
                <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                  <div>
                    <p className="text-sm font-bold text-foreground">
                      {avaliacao.pessoa_nome}
                    </p>
                    <p className="mt-0.5 text-xs text-muted-foreground">
                      {avaliacao.servico_nome || "Serviço"} {avaliacao.data ? `• ${avaliacao.data}` : ""}
                    </p>
                  </div>
                  <Stars value={avaliacao.nota} />
                </div>
                {avaliacao.comentario ? (
                  <p className="mt-3 text-sm leading-6 text-slate-700">
                    “{avaliacao.comentario}”
                  </p>
                ) : (
                  <p className="mt-3 text-sm text-muted-foreground">Sem comentário escrito.</p>
                )}
              </article>
            ))}
          </div>
        ) : (
          <div className="mt-5 rounded-2xl bg-slate-50 p-6 text-center">
            <p className="text-sm font-semibold text-slate-700">
              Você ainda não avaliou anfitriões.
            </p>
            <p className="mt-1 text-sm text-muted-foreground">
              Depois do checkout, suas avaliações enviadas aparecerão neste histórico.
            </p>
          </div>
        )}
      </section>
    </div>
  );
}
